import { Button } from '@heroui/react'
import { toast } from '@/src/lib/toast'
import { useAddToCartMutation } from '@/src/store/rtkQueries/userPostAPI'
import { useGetUserCartDataQuery } from '@/src/store/rtkQueries/userGetAPI'
import { useDispatch } from 'react-redux'
import { openDrawer } from '@/src/store/slices/allDrawerSlice'
import { useRouter } from 'nextjs-toploader/app'
import { getCartPath, getMembershipPath } from '@/src/routes/routes'
import { useAuth } from '@/src/hooks/useAuth'
import Link from 'next/link'
import { MembersOnlySVG } from '@/src/ui/AllSVG'
import { useState } from 'react'

interface AddToCartProps {
    addToCartPayload: {
        product_id: string;
        quantity: number;
        variant_sku: string;
        country: string;
        currency: string;
    }
    isAlreadyCart?: boolean
    isExclusive?: boolean
    stock?: number
}
export default function AddToCart({ addToCartPayload, isAlreadyCart = false, isExclusive = false, stock = 0 }: AddToCartProps) {
    const dispatch = useDispatch();
    const router = useRouter();
    const { isAuthenticated, isMember } = useAuth();
    const [isAddedToCart, setIsAddedToCart] = useState(false);
    const [addToCart, { isLoading }] = useAddToCartMutation();
    const { data: cartData, isLoading: isCartLoading } = useGetUserCartDataQuery(undefined, { skip: !isAuthenticated });

    // Derive live state from RTK cache — auto-updates whenever cart mutations
    // invalidate the 'Cart' tag (addToCart, removeCartItem, etc.)
    const isInCart = cartData?.data?.items?.some(
        (item) => item.product_id._id === addToCartPayload.product_id && item.variant_sku === addToCartPayload.variant_sku
    ) ?? isAlreadyCart;

    const handleAddToCart = async () => {
        try {
            const response = await addToCart(addToCartPayload).unwrap();

            if (response.http_status_code === 200 || response.http_status_code === 201) {
                toast.success(response.message || 'Item added to cart');
                setIsAddedToCart(true);
            }
        } catch (error: unknown) {
            console.error('Error adding item to cart', error);
            const err = error as { data?: { httpStatus?: number } }
            const status = err?.data?.httpStatus
            if (status === 401) {
                dispatch(openDrawer({ componentName: 'AuthDrawer', placement: 'right', className: 'rounded-none w-[500px]' }))
            }
        }
    }

    if (isExclusive && !isMember) {
        return (
            <Link
                href={getMembershipPath()}
                className="global_btn rounded_full w-full h-12 uppercase inline-flex items-center justify-center gap-2"
            >
                <MembersOnlySVG />
                Members Only — Join Now
            </Link>
        )
    }

    if (isCartLoading) {
        return (
            <div
                className="w-full h-12 rounded-full bg-muted-color animate-pulse"
                aria-busy="true"
                aria-label="Loading cart"
            />
        )
    }

    const isOutOfStock = stock <= 0;

    return (
        <>
            {isInCart || isAddedToCart ?
                <Button className="global_btn rounded_full outline_primary w-full h-12 uppercase" onClick={() => router.push(getCartPath())}>Go to cart</Button>
                : isOutOfStock ?
                    <Button className="global_btn rounded_full w-full h-12 uppercase opacity-60 cursor-not-allowed" isDisabled>
                        Out of Stock
                    </Button>
                    : <Button className="global_btn rounded_full w-full h-12 uppercase"
                        isDisabled={isLoading} onClick={() => handleAddToCart()}>
                        {isLoading ? 'Adding to cart...' : 'Add to Bag'}
                    </Button>
            }
        </>
    )
}